import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  ListItemText,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers";

import {
  type FlexibleScheduleData,
  type FlexibleScheduleFieldErrors,
  parseTimeInputValue,
  toTimeInputValue,
} from "../../flexibleSchedule";
import { DAYS_OF_WEEK } from "../../types";

/**
 * Spelled out per field rather than generically so a host form whose own data
 * extends {@link FlexibleScheduleData} can hand its own setter straight down.
 */
interface FlexibleScheduleChangeHandler {
  (field: "hasFixedStartTime" | "hasSpecificDaysOfWeek", value: boolean): void;
  (field: "estimatedStartTime", value: string): void;
  (field: "daysOfWeek", value: string[]): void;
}

interface Props {
  data: FlexibleScheduleData;
  errors?: FlexibleScheduleFieldErrors;
  onChange: FlexibleScheduleChangeHandler;
}

const inputSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
  width: "100%",
};

/**
 * The two opt-ins that turn a flexible hourly visit into a scheduled one. Shared
 * by the posting wizard and the edit form; callers decide whether to render it
 * at all via `hasFlexibleScheduleOptions`.
 */
export function MobileFlexibleScheduleFields({ data, errors, onChange }: Props) {
  const startTimeError = errors?.estimatedStartTime;
  const daysOfWeekError = errors?.daysOfWeek;

  return (
    <>
      <Stack spacing={1}>
        <FormControlLabel
          label="This visit starts at a fixed time"
          control={
            <Checkbox
              checked={data.hasFixedStartTime}
              onChange={(e) => {
                onChange("hasFixedStartTime", e.target.checked);
              }}
            />
          }
        />
        {data.hasFixedStartTime && (
          <MobileTimePicker
            label="Start time"
            value={parseTimeInputValue(data.estimatedStartTime) ?? null}
            onChange={(date) => {
              onChange(
                "estimatedStartTime",
                date && !Number.isNaN(date.getTime()) ? toTimeInputValue(date) : ""
              );
            }}
            slotProps={{
              textField: {
                sx: inputSx,
                error: Boolean(startTimeError),
                helperText: startTimeError,
              },
            }}
          />
        )}
      </Stack>

      <Stack spacing={1}>
        <FormControlLabel
          label="Specific weekday(s)"
          control={
            <Checkbox
              checked={data.hasSpecificDaysOfWeek}
              onChange={(e) => {
                onChange("hasSpecificDaysOfWeek", e.target.checked);
              }}
            />
          }
        />
        {data.hasSpecificDaysOfWeek && (
          <FormControl fullWidth sx={inputSx} error={Boolean(daysOfWeekError)}>
            <Select
              multiple
              displayEmpty
              value={data.daysOfWeek}
              inputProps={{ "aria-label": "Days of week" }}
              renderValue={(selected) =>
                selected.length > 0 ? selected.join(", ") : "Select weekdays"
              }
              onChange={(e) => {
                const { value } = e.target;
                onChange("daysOfWeek", typeof value === "string" ? value.split(",") : value);
              }}
            >
              {DAYS_OF_WEEK.map((day) => (
                <MenuItem key={day} value={day}>
                  <Checkbox checked={data.daysOfWeek.includes(day)} />
                  <ListItemText primary={day} />
                </MenuItem>
              ))}
            </Select>
            {daysOfWeekError ? <FormHelperText>{daysOfWeekError}</FormHelperText> : null}
          </FormControl>
        )}
      </Stack>
    </>
  );
}
